import type { ILanguage, ITenantLanguage } from '@/shared/api/types/tenant-types'
import { useEffect, useMemo, useState } from 'react'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'

export interface LanguageOption {
  label: string
  value: string
  isDefault: boolean
}

interface UseLanguageFilterResult {
  selectedLanguageCode: string
  setSelectedLanguageCode: (code: string) => void
  languageOptions: LanguageOption[]
  defaultLanguageCode: string
}

function toLanguageOptions(
  items: readonly (ITenantLanguage | ILanguage)[],
  languageLabel: (code: string) => string,
): LanguageOption[] {
  const optionsByCode = new Map<string, LanguageOption>()

  items
    .filter((item): item is ITenantLanguage | ILanguage => {
      const code = 'languageCode' in item ? item.languageCode : item.code
      return item.isActive && !!code?.trim()
    })
    .slice()
    .sort(
      (a, b) =>
        (('sortOrder' in a ? a.sortOrder : 0) ?? 0) - (('sortOrder' in b ? b.sortOrder : 0) ?? 0),
    )
    .forEach((item) => {
      if ('languageCode' in item) {
        const option = {
          label: item.languageName?.trim() || languageLabel(item.languageCode),
          value: item.languageCode.trim(),
          isDefault: item.isDefault,
        }
        const existing = optionsByCode.get(option.value)
        if (!existing || (!existing.isDefault && option.isDefault)) {
          optionsByCode.set(option.value, option)
        }
        return
      }
      const option = {
        label: item.nativeName?.trim() || item.name?.trim() || languageLabel(item.code),
        value: item.code.trim(),
        isDefault: item.isDefault,
      }
      const existing = optionsByCode.get(option.value)
      if (!existing || (!existing.isDefault && option.isDefault)) {
        optionsByCode.set(option.value, option)
      }
    })

  return Array.from(optionsByCode.values())
}

export function useLanguageFilter(
  items: readonly (ITenantLanguage | ILanguage)[] | undefined,
  resetKey?: string,
): UseLanguageFilterResult {
  const [selectedLanguageCode, setSelectedLanguageCode] = useState('')
  const languageLabel = useLanguageLabel()

  // * VI: languageLabel nằm trong deps để đổi ngôn ngữ dashboard là nhãn được tính lại, không kẹt bản dịch cũ.
  // * EN: languageLabel belongs in the deps so switching the dashboard language recomputes the labels instead of reusing stale ones.
  const languageOptions = useMemo<LanguageOption[]>(
    () => toLanguageOptions(items ?? [], languageLabel),
    [items, languageLabel],
  )

  const defaultLanguageCode = useMemo(
    () => languageOptions.find((item) => item.isDefault)?.value ?? languageOptions[0]?.value ?? '',
    [languageOptions],
  )

  useEffect(() => {
    if (languageOptions.length < 1) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- keep selection valid as the available options change
      setSelectedLanguageCode('')
      return
    }
    if (
      selectedLanguageCode &&
      languageOptions.some((item) => item.value === selectedLanguageCode)
    ) {
      return
    }
    setSelectedLanguageCode(defaultLanguageCode)
  }, [defaultLanguageCode, languageOptions, selectedLanguageCode, resetKey])

  return {
    selectedLanguageCode,
    setSelectedLanguageCode,
    languageOptions,
    defaultLanguageCode,
  }
}
